/**
 * @file 解答実績の取得と記録 (リーダーの問題ごとの面が使う)。
 *
 * @what: まなぶ人と資料が決まったら結果を 1 回読み、記録は即 POST して返ってきた全問の
 *   結果で差し替える。
 * @why: 記録は「その場で 1 回残す」操作なので、plan の draft + 保存バーとは分ける。
 * @failure: 取得失敗は status "error" (面は 1 行で知らせ、読む操作は止めない)。記録の失敗は
 *   actionError に文言 (詳細は console)。
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchItemResults,
  postItemResult,
  type StudyItemResultKind,
  type StudyItemResults,
} from "../api/itemResults";

export type ItemResultsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; items: StudyItemResults };

export function useItemResults(familyId: string, personId: string | null, sourceId: string) {
  const [state, setState] = useState<ItemResultsState>({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!personId) {
      setState({ status: "ready", items: {} });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    setActionError(null);
    fetchItemResults(familyId, personId, sourceId, controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) setState({ status: "ready", items });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load item results", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, personId, sourceId]);

  const record = useCallback(
    async (itemId: string, result: StudyItemResultKind, date?: string): Promise<boolean> => {
      if (!personId || busy) return false;
      setBusy(true);
      setActionError(null);
      try {
        const items = await postItemResult(
          familyId,
          personId,
          sourceId,
          itemId,
          date ? { result, date } : { result },
        );
        setState({ status: "ready", items });
        return true;
      } catch (err: unknown) {
        console.error("[study] failed to record item result", err);
        setActionError("記録できませんでした。時間を置いて再度お試しください。");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, familyId, personId, sourceId],
  );

  return { state, record, busy, actionError };
}
